import {
    Box,
    Button, Card,
    CardContent, Chip,
    IconButton,
    Table, TableBody, TableCell,
    TableContainer,
    TableHead, TablePagination, TableRow,
    TextField,
    Typography
} from "@mui/material";
import {GetApp, Search, Visibility} from "@mui/icons-material";
import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {RootState} from "../../../../../store";
import {PaginatorFactory} from "../../../../../factory";
import {ReportTypes, RiskReportTypes} from "../../../../../types";
import {ReportService} from "../../../../../services/report.service";
import {RISK_REPORT_COLUMNS} from "../../../../../builders/reports-builders";
import {calculateAge} from "../../../../../helpers";
import dayjs from "dayjs";
import Loading from "../../../../../components/loading";
import {When} from "react-if";
import NoData from "../../../../../components/no-data";
import palette from "../../../../../theme/palette";
import CloseIcon from "@mui/icons-material/Close";
import {
    getRiskFactorChipStyle,
    getPatientName, getRiskFactorTexts
} from "../../../../../helpers/crimeScore";

// Mirrors the API's searchTerm cap (fetchReportsFilters in pom-2-api). Keeping
// them equal means the search box can't build a filter the API 400s on.
const SEARCH_MAX_LENGTH = 200;

interface Props {
    setCurrentView: (view: 'search' | 'details') => void;
    handleDownload: (report: RiskReportTypes.RiskResponse) => void;
    setSelectedReport: (report: ReportTypes.Model | null) => void;
    selectedReport?: ReportTypes.Model | null;
}

export const RiskAssessmentHistory: React.FC<Props> = ({selectedReport, setCurrentView, handleDownload, setSelectedReport}) => {
    let { pagination: { total, items }, loading } = useSelector((state: RootState) => state.report);
    const [pagination, setPagination] = useState<PaginatorFactory<ReportTypes.Model>>({
        page: 0,
        perPage: 10,
    });
    const [filter, setFilter] = useState<any>({reportType : 'Intake Risk Assessment'});

    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if(!selectedReport)
            ReportService.fetchMany({ ...pagination, page: pagination.page + 1, filter });
    }, [pagination?.perPage, pagination?.page, filter]);


    // Searching has to reset to the first page, or a search run from page 3
    // asks the API for page 3 of a smaller result set and the table looks empty.
    const applySearch = (term: string) => {
        const trimmed = term.trim().slice(0, SEARCH_MAX_LENGTH);
        setPagination(pag => ({ ...pag, page: 0 }));
        setFilter(
            trimmed.length
                ? {
                    reportType: 'Intake Risk Assessment',
                    // `searchTerm` is what the fixed API understands. The legacy
                    // Prisma-style OR is sent alongside it on purpose: this repo
                    // and pom-2-api deploy separately, and the web app reaches
                    // prod first. Against an API that predates the fix the OR
                    // shape yields the same zero rows it does today (Mongo has
                    // no `OR` operator, so it is read as a literal field name)
                    // rather than an unfiltered list presented as search results.
                    // Once the API is in prod this OR can be deleted.
                    searchTerm: trimmed,
                    OR: [
                        { name: { contains: trimmed, mode: 'insensitive' } },
                        { address: { contains: trimmed, mode: 'insensitive' } },
                    ],
                }
                : { reportType: 'Intake Risk Assessment' }
        );
    };

    // Guarded against a held Enter key: each search is an expensive server-side
    // decrypt-and-match, so it must not be repeatable at key-repeat rate.
    const handleSearch = () => {
        if (loading?.fetchMany) return;
        applySearch(searchTerm);
    };

    // The X button used to only blank the text box, leaving the previous search
    // applied — clear it and reload the unfiltered list. Same in-flight guard as
    // handleSearch: both trigger the server-side decrypt-and-match.
    const handleClearSearch = () => {
        if (loading?.fetchMany) return;
        setSearchTerm('');
        applySearch('');
    };

    return (
        <Card
            sx={{
                // Same width as the hero band above it (Dan's ask: they line up).
                maxWidth: 1240,
                mx: 'auto',
                borderRadius: '24px',
                border: '1px solid #E6EBF2',
                boxShadow: '0 12px 32px -20px rgba(14,18,48,0.16)',
                '& .MuiTableHead-root .MuiTableCell-root': {
                    backgroundColor: '#F7F9FC',
                    borderBottom: '1px solid #E8EDF4',
                },
            }}
        >
            <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', color: '#0E1230' }}>
                            Past Reports
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Saved assessments, ready to reopen or export
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search by patient name or address"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                // e.repeat: a held key must not queue one search per repeat.
                                if (e.key === 'Enter' && !e.repeat) handleSearch();
                            }}
                            sx={{ width: 350 }}
                            // Capped to match the API's searchTerm limit, so the box
                            // cannot produce a filter the API rejects with a 400.
                            inputProps={{ maxLength: SEARCH_MAX_LENGTH }}
                            InputProps={{
                                endAdornment: (
                                    <IconButton onClick={handleClearSearch} size="small">
                                        <CloseIcon />
                                    </IconButton>
                                )
                            }}
                        />
                        <Button
                            variant="alternate"
                            size="small"
                            onClick={handleSearch}
                            disabled={loading?.fetchMany}
                        >
                            Search
                        </Button>
                    </Box>
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {RISK_REPORT_COLUMNS?.map((column, index) => (
                                    <TableCell key={index}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                            {column?.headerName}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <When condition={!loading.fetchMany}>
                            <TableBody>
                                {items?.map((assessment) => {
                                    const person = assessment.riskAssessmentResult?.riskAssessmentInfo?.searchedPerson;
                                    // No name on the searched person means the search ran in
                                    // address-only mode (or predates the person block). Say so
                                    // rather than "N/A", which reads as missing data and, for
                                    // name-required orgs, as a validation bypass (868kntbth).
                                    const addressOnly = !person?.firstName && !person?.lastName;
                                    const dob = person?.dateOfBirth;
                                    const age = calculateAge(dob!);
                                    return(
                                        (
                                            <TableRow key={assessment.id} hover sx={{ cursor: 'pointer' }}>
                                                <TableCell>
                                                    <Box>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={500}
                                                            color={addressOnly ? 'text.secondary' : 'text.primary'}
                                                            fontStyle={addressOnly ? 'italic' : 'normal'}
                                                        >
                                                            {addressOnly ? 'Address-only search' : getPatientName(assessment.riskAssessmentResult!)}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {addressOnly
                                                                ? 'No name or date of birth searched'
                                                                : `Age ${age ? age : '--'} • DOB: ${dob ? dob : '--'}`}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {assessment.address ?? 'N/A'}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {getRiskFactorTexts(assessment.riskAssessmentResult!)?.map((factor, index) =>
                                                            <Chip
                                                                key={index}
                                                                label={factor}
                                                                size="small"
                                                                sx={{
                                                                    fontSize: 10,
                                                                    height: 20,
                                                                    ...getRiskFactorChipStyle(factor)
                                                                }}
                                                            />
                                                        )}
                                                        {!getRiskFactorTexts(assessment.riskAssessmentResult!)?.length && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                N/A
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>

                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {assessment.sharedBy}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {assessment.createdAt ? dayjs(assessment.createdAt).format('MMMM D, YYYY') : 'N/A'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {assessment.createdAt ? dayjs(assessment.createdAt).format('h:mm A') : 'N/A'}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant="alternate"
                                                            startIcon={<Visibility />}
                                                            onClick={() => {
                                                                setSelectedReport(assessment);
                                                                setCurrentView('details');
                                                            }}
                                                        >
                                                            View
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            onClick={() => {handleDownload(assessment.riskAssessmentResult!)}}
                                                            variant="outlined-alternate"
                                                            startIcon={<GetApp />}
                                                        >
                                                            Export
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )
                                })}
                            </TableBody>
                        </When>
                    </Table>
                    <When condition={loading.fetchMany}>
                        <Loading color={palette.darkBlue}/>
                    </When>
                    <When
                        condition={
                            loading?.fetchMany === false && (!items || items?.length < 1)
                        }
                    >
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={'No reports found'}
                            description={
                                'There are no intake risk assessments.'
                            }
                        />
                    </When>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={total ?? 0}
                    rowsPerPage={pagination?.perPage}
                    page={pagination?.page}
                    onPageChange={(e, p) => setPagination(pag => ({ ...pag, page: p }))}
                    onRowsPerPageChange={(e) => setPagination(pag => ({ ...pag, page: 0, perPage: parseInt(e.target.value, 10) }))}
                />
            </CardContent>
        </Card>
    )
}
